import { Modal, NDSProvider } from "@nulogy/components";
import { ConfirmModalContextProvider } from "contexts/ConfirmModalContext";
import { GlobalStateProvider } from "contexts/GlobalStateContext";
import { NetworkStateProvider } from "contexts/NetworkStateContext";
import { UIStateProvider } from "contexts/UIStateContext";
import { WorkOrderSearchProvider } from "contexts/WorkOrderSearchContext";
import ApplicationContext from "domain/ApplicationContext";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import React from "react";
import { BrowserRouter } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import NotificationContainer from "./common/NotificationContainer";
import Pendo from "./common/Pendo";
import ErrorBoundary from "./ErrorBoundary";
import GqlProvider from "./GqlProvider";
import ApplicationLayoutContainer from "./layouts/ApplicationLayoutContainer";
import Router from "./Router";

const GlobalStyle = createGlobalStyle`
  html, body {
    height: 100%;
    overscroll-behavior-x: none;
  }

  /* Scoped to Production Scheduling's own content: unscoped, it also reaches the spark-ui chrome, where
     an unlayered host rule outranks every layered spark-ui rule (spark-ui ADR 0040). */
  .ps-app-content a {
    text-decoration: none;
  }

  /* The pages stack freely amongst themselves (a work order panel at 11, a date popper at 100000), so
     confine those to the content region. */
  .ps-app-content {
    isolation: isolate;
  }

  /* The shell's chrome claims no z-index and sits before the content in the DOM, so the page paints over
     it — including the sidebar expander, which pins itself to the viewport's full height. Isolating the
     content above is what makes a single step enough to clear all of it. */
  .spark-appshell-banner,
  .spark-appshell-topbar {
    position: relative;
    z-index: 1;
  }

  /* The shell asks only for a minimum height, so a page taller than the viewport grows the whole frame and
     the document scrolls, carrying the rail and topbar off screen. Bounding it hands the scrolling back to
     the region that owns it, and the chrome stays put. */
  .spark-appshell {
    block-size: 100dvh;
  }

  /* Pages that can't narrow past a width of their own — the availability and planned break tables — are wider
     than the page on a small window. The region hides that overflow rather than offering it, leaving the far
     side unreachable where the window used to scroll across to it. Vertical scrolling is untouched.

     Qualified by the shell, so this outranks the rule it replaces: on equal ground spark-ui's would win,
     since its stylesheet arrives with the shell and lands after this one (spark-ui ADR 0040). */
  .spark-appshell .spark-appshell-main-scroll {
    overflow-x: auto;
  }

  /* The footer stays pinned to the bottom of the window, but starts where the page does rather than at the
     window's edge, so it no longer runs under the rail. The rail's own width is a variable the shell keeps
     current, so this follows it when it collapses. Scoped to the shell, since without it the page is the
     window and the footer already spans the right thing. */
  .spark-appshell .ps-sticky-footer {
    left: var(--spark-appshell-sidenav-size);
    right: 0;
    width: auto;
  }
`;

const BranchDisplay = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  font-size: 12px;
  color: blue;
  padding: 4px;
`;

function Branch() {
  const applicationContext = useApplicationContext();
  if (applicationContext.loading) return null;
  if (!applicationContext.branch || applicationContext.branch === "main") return null;

  return <BranchDisplay>{applicationContext.branch}</BranchDisplay>;
}

function StateProviders({ children }) {
  const applicationContext = useApplicationContext();
  if (applicationContext.loading) return null;

  const sessionIdentifier = ApplicationContext.sessionIdentifier(applicationContext);

  return (
    <GlobalStateProvider>
      <UIStateProvider sessionIdentifier={sessionIdentifier}>
        <ConfirmModalContextProvider>
          <NetworkStateProvider>
            <WorkOrderSearchProvider sessionIdentifier={sessionIdentifier}>{children}</WorkOrderSearchProvider>
          </NetworkStateProvider>
        </ConfirmModalContextProvider>
      </UIStateProvider>
    </GlobalStateProvider>
  );
}

const App = (
  <ErrorBoundary>
    <NDSProvider>
      <GqlProvider>
        <StateProviders>
          <GlobalStyle />
          <Pendo>
            {/* Above the layout, so the nav's own links can route rather than reload the page. */}
            <BrowserRouter>
              <ApplicationLayoutContainer>
                <NotificationContainer />
                <Router />
                <Branch />
              </ApplicationLayoutContainer>
            </BrowserRouter>
          </Pendo>
        </StateProviders>
      </GqlProvider>
    </NDSProvider>
  </ErrorBoundary>
);

Modal.setAppElement(document.getElementById("root"));

export default App;
